import React, { useEffect, useState, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { AnnouncementOutlined } from '@mui/icons-material';

import { DashboardMetricCardWrapper } from 'components/dashboard-metric-card';
import { useCleaningHistory } from 'store/apis';
import CleaningHistoryTable from './cleaning-history/cleaning-history-table';

interface ChartCleaningHistoryProps {
  buildingId: number;
  start: string;
  end: string;
}

export const ChartCleaningHistory = ({ buildingId, start, end }: ChartCleaningHistoryProps) => {
  const [page, setPage] = useState(1);
  const [logs, setLogs] = useState<any[]>([]);

  const { data, isFetching } = useCleaningHistory({
    buildingId,
    start,
    end,
    page,
  });

  useEffect(() => {
    if (!data) {
      return;
    }

    setLogs((prev) => (page === 1 ? data.data : [...prev, ...data.data]));
  }, [data]);

  const handleLoadMore = () => {
    setPage((prev) => prev + 1);
  };

  const prevRef = useRef({
    buildingId,
    start,
    end,
  });

  useEffect(() => {
    const prev = prevRef.current;
    const changed = prev.buildingId !== buildingId || prev.start !== start || prev.end !== end;

    if (!changed) {
      return;
    }

    prevRef.current = { buildingId, start, end };

    setPage(1);
    setLogs([]);
  }, [buildingId, start, end]);

  const hasMore = !!data && data.current_page < data.last_page;

  return (
    <DashboardMetricCardWrapper
      title="Cleaning History"
      subtitle={logs.length > 0 ? `Total Records: ${logs.length}` : undefined}
      loading={isFetching && page === 1}
      loadingText="Loading cleaning history..."
    >
      {logs.length === 0 && !isFetching ? (
        <Box sx={{ textAlign: 'center' }}>
          <AnnouncementOutlined color="primary" sx={{ fontSize: 40 }} />
          <Typography color="primary" sx={{ mt: 2 }}>
            No Cleaning History Found
          </Typography>
        </Box>
      ) : (
        <CleaningHistoryTable logs={logs} onLoadMore={handleLoadMore} loading={isFetching} hasMore={hasMore} />
      )}
    </DashboardMetricCardWrapper>
  );
};
